import {
  AgentChatThreadId,
  DateTimeString,
  HexVersionId,
  UserId,
  stableEmptyArray,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { compareAsc, parseISO } from "date-fns";
import { groupBy, sortBy } from "lodash";

import { AgentChatMessageFragment } from "../../components/agent/hex-project-shared/messages/AgentChatMessage.generated";
import {
  AgentChatMessage,
  AgentChatThread,
} from "../../components/agent/shared/agentChatMessage.types";
import {
  messageFragmentToAgentChatMessage,
  threadFragmentToAgentChatThread,
} from "../../components/agent/shared/agentChatMessageTypeUtils";
import {
  AgentChatThreadFragment,
  AgentChatThreadListItemFragment,
} from "../../components/agent/shared/sharedThreadManagementUtils.generated";
import { AgentChatThreadStatus } from "../../generated/graphqlTypes";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/index.js";

type AgentChatState = {
  /**
   * A lightweight list of agent chat threads without messages or edits.
   */
  agentChatThreadsMap: Record<AgentChatThreadId, AgentChatThread>;
  /**
   * Efficient lookups to find all subagent threads for a specific parent thread.
   */
  subagentThreadIdsByParentId: Record<AgentChatThreadId, AgentChatThreadId[]>;
  /**
   * True when the initial page of agent chat threads is loading.
   */
  isLoadingAgentChatThreads: boolean;
  /**
   * True when there are more threads that can be loaded via a paginated query.
   */
  hasMoreThreads: boolean;
  /**
   * True when the next page of threads for the thread list is loading.
   */
  isLoadingMoreChatThreads: boolean;
  /**
   * The id of the currently selected thread.
   */
  selectedThreadId: AgentChatThreadId | null;
  /**
   * True when the full details of the selected thread are loading.
   */
  isLoadingSelectedThread: boolean;
  /**
   * Incremented to trigger loading more threads. Components can dispatch
   * triggerLoadMoreThreads to increment this value and useAgentChatThreads
   * will respond by loading the next page.
   */
  loadMoreThreadsTrigger: number;
};

const initialState: AgentChatState = {
  agentChatThreadsMap: {},
  subagentThreadIdsByParentId: {},
  isLoadingAgentChatThreads: false,
  hasMoreThreads: false,
  isLoadingMoreChatThreads: false,
  selectedThreadId: null,
  isLoadingSelectedThread: false,
  loadMoreThreadsTrigger: 0,
};

/**
 * Manages agent chat thread state including thread list, selection, and messages.
 */
export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState,
  reducers: {
    resetForNewHex: (
      _state,
      action: PayloadAction<{ selectedThreadId: AgentChatThreadId | null }>,
    ) => {
      // Reset all state for new hex, but preserve the stored thread selection
      return {
        ...initialState,
        subagentThreadIdsByParentId: {},
        selectedThreadId: action.payload.selectedThreadId,
      };
    },
    setAgentChatThreads: (
      state,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      // Reset and rebuild the threads map and subagent index
      state.agentChatThreadsMap = {};
      state.subagentThreadIdsByParentId = {};

      for (const thread of threads) {
        state.agentChatThreadsMap[thread.id] = thread;

        // If thread has invokedByThreadId, add to index
        if (thread.invokedByThreadId) {
          addToSubagentMap(state, thread.invokedByThreadId, thread.id);
        }
      }
    },
    appendAgentChatThreads: (
      state,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      for (const thread of threads) {
        state.agentChatThreadsMap[thread.id] = thread;

        // If thread has invokedByThreadId, add to index
        if (thread.invokedByThreadId) {
          addToSubagentMap(state, thread.invokedByThreadId, thread.id);
        }
      }
    },
    setHasMoreThreads: (state, action: PayloadAction<boolean>) => {
      state.hasMoreThreads = action.payload;
    },
    setIsLoadingAgentChatThreads: (state, action: PayloadAction<boolean>) => {
      state.isLoadingAgentChatThreads = action.payload;
    },
    setIsLoadingMoreChatThreads: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMoreChatThreads = action.payload;
    },
    triggerLoadMoreThreads: (state) => {
      state.loadMoreThreadsTrigger = state.loadMoreThreadsTrigger + 1;
    },
    setSelectedThreadId: (
      state,
      action: PayloadAction<AgentChatThreadId | null>,
    ) => {
      if (action.payload === state.selectedThreadId) {
        return;
      }

      state.selectedThreadId = action.payload;
      // Nothing to load if the thread id is null
      state.isLoadingSelectedThread = action.payload != null;
    },
    /**
     * Sets messages for a parent thread and its child threads (e.g., subagents).
     * Each thread stores only its own messages; the selector merges them at read time.
     */
    setMessagesForThread: (
      state,
      action: PayloadAction<{
        thread: AgentChatThreadFragment;
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, thread } = action.payload;

      // Set parent thread messages
      const parentMessages = thread.agentMessages.edges.map((e) => e.node);
      setThreadMessages(state, {
        threadId: thread.id,
        messages: parentMessages,
        currentUserId,
      });

      // Set child thread messages with parent relationship
      for (const child of thread.childThreads) {
        const childMessages = child.agentMessages.edges.map((e) => e.node);
        setThreadMessages(state, {
          threadId: child.id,
          messages: childMessages,
          currentUserId,
          parentThreadId: thread.id,
        });
      }

      state.isLoadingSelectedThread = false;
    },
    setIsLoadingSelectedThread: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSelectedThread = action.payload;
    },
    addMessagesToThread: (
      state,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, messages, threadId } = action.payload;

      const thread = ensureThreadExists(state, threadId, currentUserId);

      // Check if this is a subagent thread by looking at the messages
      const isSubagentThread =
        thread.invokedByThreadId != null ||
        messages.some((m) => m.invokedByThreadId != null);

      // Skip adding messages if thread is IDLE (e.g., user cancelled)
      // BUT allow messages for subagent threads to continue streaming even after
      // the thread status becomes IDLE, since the parent thread may still be active
      const existingThread = state.agentChatThreadsMap[threadId];
      if (existingThread?.status === "IDLE" && !isSubagentThread) {
        return;
      }

      const threadCopy = { ...thread };
      const newMessages: AgentChatMessage[] = messages.map(
        messageFragmentToAgentChatMessage,
      );

      // Combine existing messages and new messages, deduplicating by staticId
      threadCopy.agentMessages = mergeMessagesWithRevisions(
        threadCopy.agentMessages,
        newMessages,
      );

      // If any message has invokedByThreadId and thread doesn't have it set yet,
      // this is a subagent thread - set the relationship and update index
      if (!threadCopy.invokedByThreadId) {
        const invokedByThreadId = newMessages.find(
          (m) => m.invokedByThreadId,
        )?.invokedByThreadId;

        if (invokedByThreadId) {
          threadCopy.invokedByThreadId = invokedByThreadId;
          addToSubagentMap(state, invokedByThreadId, threadId);
        }
      }

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    updateThreadMetadata: (
      state,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        updates: {
          title: string | null;
          status: AgentChatThreadStatus;
          autoSaveHexVersionId: HexVersionId | null;
        };
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, threadId, updates } = action.payload;
      const thread = ensureThreadExists(state, threadId, currentUserId);

      const threadCopy = { ...thread, status: updates.status };

      if (updates.title) {
        threadCopy.title = updates.title;
      }

      if (updates.autoSaveHexVersionId) {
        threadCopy.autoSaveHexVersionId = updates.autoSaveHexVersionId;
      }

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    updateThreadIsSensitive: (
      state,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        sensitive: boolean;
      }>,
    ) => {
      const { sensitive, threadId } = action.payload;
      const thread = state.agentChatThreadsMap?.[threadId];
      if (!thread) return;
      thread.sensitive = sensitive;
    },
    removeThread: (
      state,
      action: PayloadAction<{ threadId: AgentChatThreadId }>,
    ) => {
      const { threadId } = action.payload;
      const thread = state.agentChatThreadsMap[threadId];

      // Clean up subagent index if this was a subagent thread
      if (thread?.invokedByThreadId) {
        removeFromSubagentMap(state, thread.invokedByThreadId, threadId);
      }

      // Also remove any subagent entries where this thread was the parent
      delete state.subagentThreadIdsByParentId[threadId];

      delete state.agentChatThreadsMap[threadId];
    },
  },
});

const selectThreads = createShallowResultSelector(
  [(state: RootState) => state.agentChat.agentChatThreadsMap],
  (threadsMap) => sortBy(Object.values(threadsMap), "createdDate").reverse(),
);

/**
 * Finds all subagent threads that were invoked by the given parent thread ID.
 * Uses the pre-built map for O(1) lookup.
 */
const selectSubagentThreads = (
  state: RootState,
  parentThreadId: AgentChatThreadId,
): readonly AgentChatThread[] => {
  const subagentIds =
    state.agentChat.subagentThreadIdsByParentId[parentThreadId] ??
    stableEmptyArray<AgentChatThreadId>();

  if (subagentIds.length === 0) {
    return stableEmptyArray<AgentChatThread>();
  }

  return subagentIds
    .map((id) => state.agentChat.agentChatThreadsMap[id])
    .filter(Boolean);
};

/**
 * Merges a parent thread's messages with all subagent messages.
 * Returns messages sorted chronologically by createdDate.
 *
 * This is the primary way to get messages for rendering - it combines:
 * 1. The parent thread's own messages
 * 2. Messages from any subagent threads (identified by invokedByThreadId)

 */
const selectThreadMessagesWithSubagents = (
  state: RootState,
  threadId: AgentChatThreadId | null,
): readonly AgentChatMessage[] => {
  if (!threadId) return stableEmptyArray<AgentChatMessage>();

  const thread = state.agentChat.agentChatThreadsMap[threadId];
  if (!thread) return stableEmptyArray<AgentChatMessage>();

  const subagentThreads = selectSubagentThreads(state, threadId);

  // If no subagent threads, return parent messages as-is (already sorted)
  if (subagentThreads.length === 0) {
    return thread.agentMessages;
  }

  // Merge all messages and sort chronologically
  const allMessages = [
    ...thread.agentMessages,
    ...subagentThreads.flatMap((t) => t.agentMessages),
  ].sort((a, b) =>
    compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
  );

  return allMessages;
};

export const agentChatSelectors = {
  selectIsLoadingThreads: (state: RootState) =>
    state.agentChat.isLoadingAgentChatThreads,
  selectIsLoadingSelectedThread: (state: RootState) =>
    state.agentChat.isLoadingSelectedThread,
  selectSelectedThreadId: (state: RootState) =>
    state.agentChat.selectedThreadId,
  selectIsLoadingMoreChatThreads: (state: RootState) =>
    state.agentChat.isLoadingMoreChatThreads,
  selectHasMoreThreads: (state: RootState) => state.agentChat.hasMoreThreads,
  selectLoadMoreThreadsTrigger: (state: RootState) =>
    state.agentChat.loadMoreThreadsTrigger,

  selectThreads,
  selectNumberOfThreads: (state: RootState) => selectThreads(state).length,

  selectSelectedThread: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectedThreadId
      ? state.agentChat.agentChatThreadsMap[selectedThreadId] || null
      : null;
  },

  /**
   * Returns the selected thread's messages merged with subagent messages.
   * Use this instead of selectSelectedThread.agentMessages for rendering.
   */
  selectSelectedThreadMessages: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectThreadMessagesWithSubagents(state, selectedThreadId);
  },
};

export const agentChatReducer = agentChatSlice.reducer;

export const {
  addMessagesToThread,
  appendAgentChatThreads,
  removeThread,
  resetForNewHex,
  setAgentChatThreads,
  setHasMoreThreads,
  setIsLoadingAgentChatThreads,
  setIsLoadingMoreChatThreads,
  setIsLoadingSelectedThread,
  setMessagesForThread,
  setSelectedThreadId,
  triggerLoadMoreThreads,
  updateThreadIsSensitive,
  updateThreadMetadata,
} = agentChatSlice.actions;

/**
 * Creates an optimistic thread when we receive updates for a thread that
 * doesn't exist locally. This can happen when we get real-time updates before
 * the thread list has been loaded.
 *
 * NOTE: This function assumes that any optimistically created threads belong to
 * the currently logged-in user.
 */
const createOptimisticThread = (
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
): AgentChatThread => {
  return {
    id: threadId,
    title: null,
    createdDate: new Date().toISOString() as DateTimeString,
    status: "IDLE" as AgentChatThreadStatus,
    autoSaveHexVersionId: null,
    agentMessages: [],
    editsToReview: [],
    userId: currentUserId,
    invokedByThreadId: null,
    lastUsedEffortLevel: null,
  };
};

/**
 * Ensures a thread exists in the state, creating it optimistically if needed.
 * Returns the thread (existing or newly created).
 */
const ensureThreadExists = (
  state: AgentChatState,
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
): AgentChatThread => {
  let thread = state.agentChatThreadsMap[threadId];

  if (!thread) {
    thread = createOptimisticThread(threadId, currentUserId);
    state.agentChatThreadsMap[threadId] = thread;
  }

  return thread;
};

/**
 * Adds a subagent thread to the parent index if not already present.
 */
function addToSubagentMap(
  state: AgentChatState,
  parentThreadId: AgentChatThreadId,
  subagentThreadId: AgentChatThreadId,
): void {
  if (!state.subagentThreadIdsByParentId[parentThreadId]) {
    state.subagentThreadIdsByParentId[parentThreadId] = [];
  }
  if (
    !state.subagentThreadIdsByParentId[parentThreadId].includes(
      subagentThreadId,
    )
  ) {
    state.subagentThreadIdsByParentId[parentThreadId].push(subagentThreadId);
  }
}

/**
 * Removes a subagent thread from the parent index.
 */
function removeFromSubagentMap(
  state: AgentChatState,
  parentThreadId: AgentChatThreadId,
  subagentThreadId: AgentChatThreadId,
): void {
  const subagentIds = state.subagentThreadIdsByParentId[parentThreadId];
  if (subagentIds) {
    state.subagentThreadIdsByParentId[parentThreadId] = subagentIds.filter(
      (id) => id !== subagentThreadId,
    );
    // Clean up empty arrays
    if (state.subagentThreadIdsByParentId[parentThreadId].length === 0) {
      delete state.subagentThreadIdsByParentId[parentThreadId];
    }
  }
}

/**
 * Sets messages for a thread in state, optionally establishing parent relationship.
 * Deduplicates messages by staticId, keeping the most recent for streaming updates.
 */
function setThreadMessages(
  state: AgentChatState,
  {
    currentUserId,
    messages,
    parentThreadId,
    threadId,
  }: {
    threadId: AgentChatThreadId;
    messages: readonly AgentChatMessageFragment[];
    currentUserId: UserId | null;
    parentThreadId?: AgentChatThreadId;
  },
): void {
  const thread = ensureThreadExists(state, threadId, currentUserId);
  const copy = { ...thread };

  const newMessages = messages.map(messageFragmentToAgentChatMessage);
  copy.agentMessages = mergeMessagesWithRevisions(
    copy.agentMessages,
    newMessages,
  );

  if (parentThreadId && !copy.invokedByThreadId) {
    copy.invokedByThreadId = parentThreadId;
    addToSubagentMap(state, parentThreadId, threadId);
  }

  state.agentChatThreadsMap[threadId] = copy;
}

/**
 * Merges existing messages with new messages, deduplicating by staticId.
 * For messages with the same staticId, keeps the one with the latest createdDate.
 * This handles streaming previews being replaced by newer updates or persisted messages.
 */
function mergeMessagesWithRevisions(
  existingMessages: readonly AgentChatMessage[],
  newMessages: readonly AgentChatMessage[],
): AgentChatMessage[] {
  const allMessages = [...existingMessages, ...newMessages];
  const messagesByStaticId = groupBy(allMessages, "staticId");

  return Object.values(messagesByStaticId)
    .map((messagesWithSameStaticId) => {
      // For messages with the same staticId, keep the one with the latest createdDate
      return messagesWithSameStaticId.reduce((latest, current) => {
        const latestDate = parseISO(latest.createdDate);
        const currentDate = parseISO(current.createdDate);
        return compareAsc(currentDate, latestDate) >= 0 ? current : latest;
      });
    })
    .sort((a, b) =>
      compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
    );
}
